import React, { useState, useEffect } from 'react';
import { getTokenForRequest } from '../../utils/apiAuth';
import './LessonPlan.css';

/**
 * ScaffoldManagementModal - React implementation of scaffold management
 * Matches Angular template: link-scaffold-modal.html
 */
const ScaffoldManagementModal = ({ 
  show, 
  onHide, 
  scaffoldData,
  onScaffoldDataUpdate,
  onOpenEvaluationEditor
}) => {
  const [selectedClass, setSelectedClass] = useState('0');
  const [allScaffolds, setAllScaffolds] = useState([]);
  const [classes, setClasses] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewScaffold, setPreviewScaffold] = useState(null);
  const [editingScaffold, setEditingScaffold] = useState(null);
  const [editingStudentDesc, setEditingStudentDesc] = useState('');
  const [hasEvaluation, setHasEvaluation] = useState(false);
  const [evaluationLoading, setEvaluationLoading] = useState(false);
  const [activePreviewTab, setActivePreviewTab] = useState('preview');

  useEffect(() => {
    if (show && scaffoldData) {
      // Use pre-loaded scaffold data from parent component
      setAllScaffolds(scaffoldData.AllScaffolds || []);
      loadClasses();
      checkHasEvaluation();
    }
  }, [show, scaffoldData]);

  /**
   * Check if topic has evaluation
   */
  const checkHasEvaluation = async () => {
    try {
      setEvaluationLoading(true);
      const token = getTokenForRequest();
      const encodedToken = window.encodeToken(token);

      const response = await fetch('./webservices/Evaluation.svc/HasEvaluation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: encodedToken,
          topicId: scaffoldData.topicID,
          msId: scaffoldData.msID
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to check evaluation status');
      }

      const data = await response.json();
      console.log('HasEvaluation result:', data);
      
      setHasEvaluation(data === true || data === 'true');

    } catch (error) {
      console.error('Error checking evaluation status:', error);
      setHasEvaluation(false);
    } finally {
      setEvaluationLoading(false);
    }
  };


  /**
   * Load available classes
   */
  const loadClasses = async () => {
    try {
      const token = getTokenForRequest();
      
      const response = await fetch('./webservices/Organization.svc/GetClasses', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: window.encodeToken(token),
          modulesemesterid: scaffoldData.msID
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Classes loaded:', result);
      setClasses(result || []);
    } catch (error) {
      console.error('Error loading classes:', error);
      // Don't show error for classes as it's not critical
    }
  };

  /**
   * Handle scaffold preview
   */
  const handleScaffoldPreview = (scaffold) => {
    console.log('Preview scaffold:', scaffold);
    setPreviewScaffold(scaffold);
    setShowPreview(true);
  };

  /**
   * Close preview modal
   */
  const handleClosePreview = () => {
    setShowPreview(false);
    setPreviewScaffold(null);
  };

  /**
   * Strip HTML tags from text content
   */
  const stripHtmlTags = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  /**
   * Truncate text to specified length
   */
  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    const cleanText = stripHtmlTags(text);
    return cleanText.length > maxLength ? cleanText.substring(0, maxLength) + '...' : cleanText;
  };

  /**
   * Get file type from scaffold
   */
  const getFileType = (scaffold) => {
    const type = scaffold.TypeDesc || scaffold.scaffoldType || '';
    const filename = scaffold.Filename || scaffold.filename || '';
    const fileId = scaffold.FileID || scaffold.fileId || '';
    
    // Check file extension from Filename first
    if (filename) {
      const extension = filename.split('.').pop()?.toLowerCase();
      if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) return 'video';
      if (['pdf'].includes(extension)) return 'pdf';
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) return 'image';
    }
    
    // Check file extension from FileID URL as fallback
    if (fileId) {
      const urlPath = fileId.split('/').pop() || '';
      const extension = urlPath.split('.').pop()?.toLowerCase();
      if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) return 'video';
      if (['pdf'].includes(extension)) return 'pdf';
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) return 'image';
    }
    
    // Check type description
    if (type.toLowerCase().includes('video')) return 'video';
    if (type.toLowerCase().includes('pdf')) return 'pdf';
    if (type.toLowerCase().includes('image') || type.toLowerCase().includes('picture')) return 'image';
    
    return 'unknown';
  };

  /**
   * Get file URL for preview
   */
  const getFileUrl = (scaffold) => {
    // FileID contains the filename/path, need to prepend CloudFront base URL
    const fileId = scaffold.FileID || scaffold.fileId || null;
    if (!fileId) return null;
    
    // If it's already a complete URL, return as is
    if (fileId.startsWith('http://') || fileId.startsWith('https://')) {
      return fileId;
    }
    
    // Otherwise, prepend the CloudFront base URL
    const baseUrl = 'https://d6adgef862n1y.cloudfront.net/';
    const fileUrl = baseUrl + fileId;
    
    console.log('Scaffold FileID:', scaffold.FileID);
    console.log('Constructed file URL:', fileUrl);
    return fileUrl;
  };

  // Get file path for Google Docs viewer (without base URL) - matches socrates-ui.js pattern
  const getFilePathForGoogleDocs = (scaffold) => {
    const fileId = scaffold.FileID || scaffold.fileId || null;
    if (!fileId) return '';
    
    // If FileID already contains a full URL, extract just the path
    if (fileId.startsWith('https://d6adgef862n1y.cloudfront.net/')) {
      return fileId.replace('https://d6adgef862n1y.cloudfront.net/', '');
    }
    
    // If it's already just a path, return as is
    return fileId;
  };

  /**
   * Render preview content based on file type
   */
  const renderPreviewContent = (scaffold) => {
    const fileType = getFileType(scaffold);
    const fileUrl = getFileUrl(scaffold);
    
    // Check for Camtasia video first
    if (scaffold.CamtasiaID && scaffold.CamtasiaID > 0) {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p style={{ marginBottom: '15px', fontSize: '14px', color: '#333' }}>
            Camtasia Video Preview
          </p>
          <button 
            className="btn btn-primary"
            onClick={() => window.open(`Camtasia.aspx?camtasiaID=${scaffold.CamtasiaID}`, '_blank')}
          >
            Open Camtasia Player
          </button>
          <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
            Camtasia ID: {scaffold.CamtasiaID}
          </p>
        </div>
      );
    }
    
    if (!fileUrl) {
      return (
        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
          <p>No preview available</p>
          <p style={{ fontSize: '12px' }}>File URL not available</p>
        </div>
      );
    }
    
    switch (fileType) {
      case 'video':
        return (
          <div style={{ textAlign: 'center' }}>
            <video 
              controls 
              style={{ maxWidth: '100%', maxHeight: '400px' }}
              preload="metadata"
            >
              <source src={fileUrl} type="video/mp4" />
              <source src={fileUrl} type="video/webm" />
              <source src={fileUrl} type="video/ogg" />
              Your browser does not support the video tag.
            </video>
            <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
              Video: {scaffold.Filename || scaffold.filename || 'Unknown filename'}
            </p>
          </div>
        );
        
      case 'pdf':
        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '15px' }}>
              <p style={{ fontSize: '14px', color: '#333', marginBottom: '10px' }}>
                PDF Preview Options
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button 
                  className="btn btn-sm btn-primary"
                  onClick={() => {
                    const iframe = document.querySelector('#pdf-preview-iframe');
                    if (iframe) {
                      iframe.src = fileUrl;
                    }
                  }}
                >
                  Direct PDF
                </button>
                <button 
                  className="btn btn-sm btn-secondary"
                  onClick={() => {
                    const iframe = document.querySelector('#pdf-preview-iframe');
                    if (iframe) {
                      // Use the same pattern as socrates-ui.js: fileServerUrl + filePath
                      const filePath = getFilePathForGoogleDocs(scaffold);
                      const fileServerUrl = "https://d6adgef862n1y.cloudfront.net/";
                      const googleDocsUrl = `https://docs.google.com/gview?url=${fileServerUrl}${filePath}&embedded=true`;
                      console.log('Google Docs URL:', googleDocsUrl);
                      console.log('File path for Google Docs:', filePath);
                      console.log('File server URL:', fileServerUrl);
                      iframe.src = googleDocsUrl;
                    }
                  }}
                >
                  Google Docs Viewer
                </button>
                <a 
                  href={fileUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-success"
                >
                  Open in New Tab
                </a>
              </div>
            </div>
            <iframe 
              id="pdf-preview-iframe"
              src={fileUrl}
              style={{ width: '100%', height: '500px', border: '1px solid #ddd' }}
              title="PDF Preview"
            />
            <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
              PDF: {scaffold.Filename || scaffold.filename || 'Unknown filename'}
            </p>
          </div>
        );
        
      case 'image':
        return (
          <div style={{ textAlign: 'center' }}>
            <img 
              src={fileUrl}
              alt="Scaffold Preview"
              style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain' }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div style={{ display: 'none', padding: '20px', color: '#666' }}>
              <p>Image could not be loaded</p>
              <p style={{ fontSize: '12px' }}>File: {scaffold.Filename || scaffold.filename}</p>
            </div>
            <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
              Image: {scaffold.Filename || scaffold.filename || 'Unknown filename'}
            </p>
          </div>
        );
        
      default:
        return (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            <p>Preview not supported for this file type</p>
            <p style={{ fontSize: '12px' }}>
              Type: {scaffold.TypeDesc || scaffold.scaffoldType || 'Unknown'}
            </p>
            <p style={{ fontSize: '12px' }}>
              File: {scaffold.Filename || scaffold.filename || 'Unknown filename'}
            </p>
            <a 
              href={fileUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-primary btn-sm"
              style={{ marginTop: '10px' }}
            >
              Download File
            </a>
          </div>
        );
    }
  };

  /**
   * Handle editing student description
   */
  const handleEditStudentDescription = (scaffold) => {
    setEditingScaffold(scaffold);
    setEditingStudentDesc(scaffold.StudentDescription || scaffold.Description || '');
  };

  /**
   * Handle saving student description
   */
  const handleSaveStudentDescription = async () => {
    try {
      const token = getTokenForRequest();
      
      // Call API to update scaffold with new student description
      const response = await fetch('./webservices/Scaffold.svc/UpdateScaffold', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: window.encodeToken(token),
          scaffoldId: editingScaffold.ID,
          description: `<span class="student-desp" title="${editingStudentDesc}"></span>`,
          filePath: editingScaffold.FilePath || editingScaffold.filePath || '',
          scaffoldType: editingScaffold.TypeDesc || editingScaffold.scaffoldType || 'PDF',
          topicID: scaffoldData.topicID,
          status: editingScaffold.Status || 'A',
          screenshot: editingScaffold.Screenshot || null,
          videoType: editingScaffold.VideoType || null
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Scaffold updated successfully:', result);

      // Update the scaffold data with the new description (which contains the student description)
      const updatedMsScaffolds = scaffoldData.MsScaffolds.map(scaffold => 
        scaffold.ID === editingScaffold.ID 
          ? { 
              ...scaffold, 
              Description: `<span class="student-desp" title="${editingStudentDesc}"></span>`,
              StudentDescription: editingStudentDesc 
            }
          : scaffold
      );

      const updatedScaffoldData = {
        ...scaffoldData,
        MsScaffolds: updatedMsScaffolds
      };

      // Update parent component's scaffold data
      if (onScaffoldDataUpdate) {
        onScaffoldDataUpdate(updatedScaffoldData);
      }

      // Close editing mode
      setEditingScaffold(null);
      setEditingStudentDesc('');
      
      // Student description updated successfully

    } catch (error) {
      console.error('Error updating scaffold:', error);
      alert('Failed to update scaffold');
    }
  };

  /**
   * Handle canceling student description edit
   */
  const handleCancelEdit = () => {
    setEditingScaffold(null);
    setEditingStudentDesc('');
  };

  /**
   * Handle adding scaffold to current setup
   */
  const handleAddScaffold = async (scaffold) => {
    try {
      console.log('Adding scaffold to topic:', scaffold);
      
      const token = getTokenForRequest();
      
      // Include all previously added scaffold IDs plus the new one
      const existingScaffoldIDs = scaffoldData.MsScaffolds.map(s => s.ID);
      const scaffoldIDs = [...existingScaffoldIDs, scaffold.ID];
      
      console.log('Adding scaffolds with IDs:', scaffoldIDs);
      
      // Add scaffold to module semester topic
      const response = await fetch('./webservices/Scaffold.svc/AddScaffoldsToModuleSemester', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: window.encodeToken(token),
          scaffoldIds: scaffoldIDs,
          topicId: scaffoldData.topicID,
          msId: scaffoldData.msID
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Scaffold added successfully:', result);

      // After successful add, call GetModuleSemesterScaffolds to get updated existing scaffolds
      const updatedExistingScaffoldsResponse = await fetch(`./webservices/Scaffold.svc/GetModuleSemesterScaffolds?token=${window.encodeToken(token)}&topicId=${scaffoldData.topicID}&msId=${scaffoldData.msID}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!updatedExistingScaffoldsResponse.ok) {
        throw new Error(`HTTP error! status: ${updatedExistingScaffoldsResponse.status}`);
      }

      const updatedExistingScaffoldsResult = await updatedExistingScaffoldsResponse.json();
      console.log('Updated existing scaffolds received:', updatedExistingScaffoldsResult);

      // Update the scaffold data with fresh existing scaffolds from API
      const updatedScaffoldData = {
        ...scaffoldData,
        MsScaffolds: updatedExistingScaffoldsResult.Scaffolds || []
      };
      
      // Update parent component's scaffold data
      if (onScaffoldDataUpdate) {
        onScaffoldDataUpdate(updatedScaffoldData);
      }
      
      // Scaffold added successfully to topic

    } catch (error) {
      console.error('Error adding scaffold:', error);
      alert('Failed to add scaffold to topic');
    }
  };

  /**
   * Handle removing scaffold from current setup
   * Matches Angular implementation: removes from local array and calls AddScaffoldsToModuleSemester with remaining IDs
   */
  const handleRemoveScaffold = async (scaffold) => {
    try {
      console.log('Removing scaffold from topic:', scaffold);
      
      const token = getTokenForRequest();
      
      // Remove scaffold from local MsScaffolds array (like Angular does)
      const updatedMsScaffolds = scaffoldData.MsScaffolds.filter(s => s.ID !== scaffold.ID);
      
      // Get remaining scaffold IDs (excluding the removed one)
      const remainingScaffoldIDs = updatedMsScaffolds.map(s => s.ID);
      
      console.log('Remaining scaffold IDs after removal:', remainingScaffoldIDs);
      
      // Call AddScaffoldsToModuleSemester with remaining scaffold IDs (like Angular does)
      const response = await fetch('./webservices/Scaffold.svc/AddScaffoldsToModuleSemester', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: window.encodeToken(token),
          scaffoldIds: remainingScaffoldIDs,
          topicId: scaffoldData.topicID,
          msId: scaffoldData.msID
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Scaffolds updated successfully:', result);

      // Update the scaffold data with the updated MsScaffolds array
      const updatedScaffoldData = {
        ...scaffoldData,
        MsScaffolds: updatedMsScaffolds
      };
      
      // Update parent component's scaffold data
      if (onScaffoldDataUpdate) {
        onScaffoldDataUpdate(updatedScaffoldData);
      }
      
      // Scaffold removed successfully from topic

    } catch (error) {
      console.error('Error removing scaffold:', error);
      alert('Failed to remove scaffold from topic');
    }
  };

  /**
   * Call countAllQ API to get question statistics
   */
  const callCountAllQ = async () => {
    try {
      const token = getTokenForRequest();
      const encodedToken = window.encodeToken(token);

      const response = await fetch('./webservices/Question.svc/countAllQ', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: encodedToken,
          topicId: scaffoldData.topicID
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to get question statistics');
      }

      const data = await response.json();
      console.log('Question statistics loaded:', data);
      
      // Store the statistics for the evaluation editor
      // This data will be used by the evaluation editor modal
      window.evaluationQuestionStats = data;
      
    } catch (error) {
      console.error('Error loading question statistics:', error);
      throw error;
    }
  };

  /**
   * Call GetEvaluation API to get evaluation data
   */
  const callGetEvaluation = async () => {
    try {
      const token = getTokenForRequest();
      const encodedToken = window.encodeToken(token);

      const response = await fetch('./webservices/Evaluation.svc/GetEvaluation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: encodedToken,
          topicId: scaffoldData.topicID,
          msId: scaffoldData.msID
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to get evaluation data');
      }

      const data = await response.json();
      console.log('Evaluation data loaded:', data);
      
      // Store the evaluation data for the evaluation editor
      // This data will be used by the evaluation editor modal
      window.evaluationData = data;
      
    } catch (error) {
      console.error('Error loading evaluation data:', error);
      throw error;
    }
  };

  /**
   * Handle creating evaluation
   */
  const handleCreateEvaluation = async () => {
    try {
      console.log('Create evaluation for topic:', scaffoldData?.topicName);
      
      const token = getTokenForRequest();
      const encodedToken = window.encodeToken(token);

      // Step 1: Create the evaluation
      const createResponse = await fetch('./webservices/Evaluation.svc/CreateEvaluation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: encodedToken,
          topicId: scaffoldData.topicID,
          msId: scaffoldData.msID
        }),
        credentials: 'include',
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create evaluation');
      }

      console.log('Evaluation created successfully');
      
      // Step 2: Call countAllQ API
      await callCountAllQ();
      
      // Step 3: Call GetEvaluation API
      await callGetEvaluation();
      
      // Close the scaffold modal
      onHide();
      
      // Trigger the evaluation editor modal via parent component
      if (onOpenEvaluationEditor) {
        onOpenEvaluationEditor({
          topicId: scaffoldData.topicID,
          moduleSemesterId: scaffoldData.msID,
          topicName: scaffoldData.topicName
        });
      } else {
        console.warn('onOpenEvaluationEditor prop not provided');
      }
      
    } catch (error) {
      console.error('Error creating evaluation:', error);
      alert('Failed to create evaluation for topic ' + scaffoldData.topicName);
    }
  };

  /**
   * Handle editing evaluation
   */
  const handleEditEvaluation = async () => {
    try {
      console.log('Edit evaluation for topic:', scaffoldData?.topicName);
      
      // Step 1: Call countAllQ API
      await callCountAllQ();
      
      // Step 2: Call GetEvaluation API
      await callGetEvaluation();
      
      // Close the scaffold modal
      onHide();
      
      // Trigger the evaluation editor modal via parent component
      if (onOpenEvaluationEditor) {
        onOpenEvaluationEditor({
          topicId: scaffoldData.topicID,
          moduleSemesterId: scaffoldData.msID,
          topicName: scaffoldData.topicName
        });
      } else {
        console.warn('onOpenEvaluationEditor prop not provided');
      }
      
    } catch (error) {
      console.error('Error preparing evaluation for editing:', error);
      alert('Failed to load evaluation data');
    }
  };

  if (!show || !scaffoldData) {
    return null;
  }

  return (
    <div className="modal fade show react-modal-fix scaffold-management-modal" style={{ display: 'block' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">
              Edit Scaffolds for <b>{scaffoldData.topicName}</b>
            </h4>
            <button 
              type="button" 
              className="btn-close-x" 
              aria-label="Close"
              onClick={onHide}
            >×</button>
          </div>
          
          <div className="modal-body">
            <div className="container-fluid">
              <div className="row">
                {/* Existing Scaffolds */}
                <div className="col-sm-6">
                  <h4>Existing Scaffolds</h4>
                  <h5 className="sub-title">Drag Scaffold to Reorder</h5>
                  
                  <span>
                    For 
                    <select 
                      value={selectedClass} 
                      onChange={(e) => setSelectedClass(e.target.value)}
                    >
                      <option value="0">Entire Module</option>
                      {classes.map((cls, index) => (
                        <option key={index} value={cls.ID}>
                          Class: {cls.Name}
                        </option>
                      ))}
                    </select>
                  </span>

                  {/* Module Scaffolds */}
                  {selectedClass === '0' && (
                    <div className="all-scaffolds-added">
                      {scaffoldData.MsScaffolds && scaffoldData.MsScaffolds.length > 0 ? (
                        scaffoldData.MsScaffolds.map((scaffold, index) => (
                          <div key={index} className="scaffold-item">
                            <div className="scaffold-type">
                              {scaffold.TypeDesc}
                            </div>
                            <div 
                              className="scaffold-description"
                              onClick={() => handleScaffoldPreview(scaffold)}
                              style={{ cursor: 'pointer' }}
                              title="Click to preview"
                            >
                              {truncateText(scaffold.Description, 80)}
                            </div>
                            <div className="scaffold-student-desc">
                              {editingScaffold && editingScaffold.ID === scaffold.ID ? (
                                <div>
                                  <label style={{ fontSize: '10px', fontWeight: 'bold' }}>Student:</label>
                                  <textarea
                                    value={editingStudentDesc}
                                    onChange={(e) => setEditingStudentDesc(e.target.value)}
                                    style={{
                                      width: '100%',
                                      minHeight: '40px',
                                      fontSize: '11px',
                                      padding: '4px',
                                      border: '1px solid #ccc',
                                      borderRadius: '3px',
                                      resize: 'vertical'
                                    }}
                                    placeholder="Enter student description..."
                                  />
                                  <div style={{ marginTop: '5px', textAlign: 'right' }}>
                                    <button 
                                      className="btn btn-xs btn-success"
                                      onClick={handleSaveStudentDescription}
                                      style={{ marginRight: '5px', fontSize: '10px', padding: '2px 6px' }}
                                    >
                                      Save
                                    </button>
                                    <button 
                                      className="btn btn-xs btn-default"
                                      onClick={handleCancelEdit}
                                      style={{ fontSize: '10px', padding: '2px 6px' }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <span style={{ fontSize: '10px', fontWeight: 'bold' }}>Student: </span>
                                  <span 
                                    onClick={() => handleEditStudentDescription(scaffold)}
                                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                    title="Click to edit"
                                  >
                                    {truncateText(scaffold.StudentDescription || scaffold.Description, 60)}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="scaffold-actions">
                              <button 
                                className="btn btn-sm btn-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveScaffold(scaffold);
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                      <p className="empty-state">
                        No scaffolds assigned to this topic
                      </p>
                      )}
                    </div>
                  )}

                  {/* Class Scaffolds */}
                  {selectedClass !== '0' && (
                    <div className="all-scaffolds-added">
                      <p className="empty-state">
                        Class-specific scaffolds will be shown here
                      </p>
                    </div>
                  )}
                </div>

                {/* All Scaffolds */}
                <div className="col-sm-6">
                  <h4>All Scaffolds</h4>
                  <h5 className="sub-title">Click to preview</h5>

                  {/* Search */}
                  <span>
                    <input 
                      placeholder="Search Keyword" 
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  </span>

                  <div className="all-scaffolds-toadd">
                    {loading ? (
                      <p>Loading scaffolds...</p>
                    ) : error ? (
                      <p style={{ color: 'red' }}>{error}</p>
                    ) : (
                      allScaffolds
                        .filter(scaffold => 
                          scaffold.Status === 'A' && 
                          scaffold.Description.toLowerCase().includes(searchText.toLowerCase()) &&
                          !scaffoldData.MsScaffolds.some(existing => existing.ID === scaffold.ID)
                        )
                        .map((scaffold, index) => (
                          <div key={index} className="scaffold-item"
                          onClick={() => handleScaffoldPreview(scaffold)}
                          >
                            <div className="scaffold-type">
                              {scaffold.TypeDesc}
                            </div>
                            <div className="scaffold-description">
                              {truncateText(scaffold.Description, 80)}
                            </div>
                            <div className="scaffold-student-desc">
                              Student: {truncateText(scaffold.StudentDescription || scaffold.Description, 60)}
                            </div>
                            <div className="scaffold-actions">
                              <button 
                                className="btn btn-sm btn-success"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddScaffold(scaffold);
                                }}
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            {evaluationLoading ? (
              <button 
                type="button" 
                className="btn btn-primary pull-left"
                disabled
              >
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Checking...
              </button>
            ) : hasEvaluation ? (
              <button 
                type="button" 
                className="btn btn-primary pull-left"
                onClick={handleEditEvaluation}
              >
                Edit Evaluation
              </button>
            ) : (
              <button 
                type="button" 
                className="btn btn-primary pull-left"
                onClick={handleCreateEvaluation}
              >
                Create Evaluation
              </button>
            )}

            <button 
              type="button" 
              className="btn btn-default"
              onClick={onHide}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Scaffold Preview Modal */}
      {showPreview && previewScaffold && (
        <div className="modal fade show react-modal-fix scaffold-preview-modal" style={{ display: 'block', zIndex: 1060 }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderBottom: 'none',
                padding: '20px 24px'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    display: 'inline-block',
                    padding: '4px 12px',
                    backgroundColor: 'rgba(255,255,255,0.25)',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {previewScaffold.TypeDesc || 'Scaffold'}
                  </div>
                  <h4 className="modal-title" style={{ 
                    margin: '8px 0 0 0',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: 'white',
                    lineHeight: '1.4'
                  }}>
                    {stripHtmlTags(previewScaffold.Description) || 'Scaffold Preview'}
                  </h4>
                </div>
                <button 
                  type="button" 
                  className="btn-close-x btn-close-x-white" 
                  aria-label="Close"
                  onClick={handleClosePreview}
                >×</button>
              </div>
              
              <div className="modal-body" style={{ padding: '0' }}>
                {/* Tabs Navigation */}
                <ul className="nav nav-tabs scaffold-preview-tabs" style={{
                  borderBottom: '2px solid #e9ecef',
                  padding: '0 24px',
                  margin: '0'
                }}>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activePreviewTab === 'preview' ? 'active' : ''}`}
                      onClick={() => setActivePreviewTab('preview')}
                      style={{
                        border: 'none',
                        borderBottom: activePreviewTab === 'preview' ? '3px solid #667eea' : '3px solid transparent',
                        background: 'transparent',
                        color: activePreviewTab === 'preview' ? '#667eea' : '#6c757d',
                        fontWeight: activePreviewTab === 'preview' ? '600' : '400',
                        padding: '12px 20px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      📹 Preview
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activePreviewTab === 'details' ? 'active' : ''}`}
                      onClick={() => setActivePreviewTab('details')}
                      style={{
                        border: 'none',
                        borderBottom: activePreviewTab === 'details' ? '3px solid #667eea' : '3px solid transparent',
                        background: 'transparent',
                        color: activePreviewTab === 'details' ? '#667eea' : '#6c757d',
                        fontWeight: activePreviewTab === 'details' ? '600' : '400',
                        padding: '12px 20px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      ℹ️ Details
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activePreviewTab === 'info' ? 'active' : ''}`}
                      onClick={() => setActivePreviewTab('info')}
                      style={{
                        border: 'none',
                        borderBottom: activePreviewTab === 'info' ? '3px solid #667eea' : '3px solid transparent',
                        background: 'transparent',
                        color: activePreviewTab === 'info' ? '#667eea' : '#6c757d',
                        fontWeight: activePreviewTab === 'info' ? '600' : '400',
                        padding: '12px 20px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      📋 Information
                    </button>
                  </li>
                </ul>

                {/* Tab Content */}
                <div style={{ padding: '24px' }}>
                  {/* Preview Tab */}
                  {activePreviewTab === 'preview' && (
                    <div className="tab-content-preview">
                      <div className="scaffold-preview-section" style={{ 
                        marginBottom: '24px',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        padding: '24px',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                      }}>
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: '16px',
                          paddingBottom: '12px',
                          borderBottom: '2px solid #f0f0f0'
                        }}>
                          <h5 style={{ 
                            margin: '0',
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#333',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span>🎬</span>
                            Content Preview ({getFileType(previewScaffold).toUpperCase()})
                          </h5>
                          {previewScaffold.Status === 'A' && (
                            <span style={{
                              padding: '4px 12px',
                              backgroundColor: '#d4edda',
                              color: '#155724',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}>
                              ✓ Active
                            </span>
                          )}
                        </div>
                        {renderPreviewContent(previewScaffold)}
                      </div>

                      {/* Student Description Card */}
                      <div style={{
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        padding: '20px',
                        backgroundColor: '#f8f9fa',
                        marginBottom: '20px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '12px',
                          color: '#495057',
                          fontWeight: '600',
                          fontSize: '14px'
                        }}>
                          <span>👤</span>
                          Student Description
                        </div>
                        <div style={{
                          fontSize: '15px',
                          color: '#212529',
                          lineHeight: '1.6',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {stripHtmlTags(previewScaffold.StudentDescription || previewScaffold.Description) || 'No student description available.'}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Details Tab */}
                  {activePreviewTab === 'details' && (
                    <div className="tab-content-details">
                      <div className="row">
                        <div className="col-md-6">
                          {previewScaffold.Instructions && (
                            <div style={{
                              border: '2px solid #e9ecef',
                              borderRadius: '8px',
                              padding: '20px',
                              marginBottom: '20px',
                              backgroundColor: '#fff',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '12px',
                                color: '#667eea',
                                fontWeight: '600',
                                fontSize: '14px'
                              }}>
                                <span>📝</span>
                                Instructions
                              </div>
                              <div style={{
                                fontSize: '14px',
                                color: '#495057',
                                lineHeight: '1.6',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                              }}>
                                {stripHtmlTags(previewScaffold.Instructions)}
                              </div>
                            </div>
                          )}

                          {previewScaffold.ExpectedOutcome && (
                            <div style={{
                              border: '2px solid #e9ecef',
                              borderRadius: '8px',
                              padding: '20px',
                              marginBottom: '20px',
                              backgroundColor: '#fff',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '12px',
                                color: '#28a745',
                                fontWeight: '600',
                                fontSize: '14px'
                              }}>
                                <span>🎯</span>
                                Expected Outcome
                              </div>
                              <div style={{
                                fontSize: '14px',
                                color: '#495057',
                                lineHeight: '1.6',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                              }}>
                                {stripHtmlTags(previewScaffold.ExpectedOutcome)}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="col-md-6">
                          <div style={{
                            border: '2px solid #e9ecef',
                            borderRadius: '8px',
                            padding: '20px',
                            marginBottom: '20px',
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              marginBottom: '16px',
                              color: '#495057',
                              fontWeight: '600',
                              fontSize: '14px',
                              paddingBottom: '12px',
                              borderBottom: '2px solid #f0f0f0'
                            }}>
                              <span>📄</span>
                              Educator Description
                            </div>
                            <div style={{
                              fontSize: '14px',
                              color: '#495057',
                              lineHeight: '1.6',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}>
                              {stripHtmlTags(previewScaffold.Description) || 'No description available.'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Information Tab */}
                  {activePreviewTab === 'info' && (
                    <div className="tab-content-info">
                      <div className="row">
                        <div className="col-md-6">
                          <div style={{
                            border: '2px solid #e9ecef',
                            borderRadius: '8px',
                            padding: '20px',
                            marginBottom: '20px',
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                          }}>
                            <h6 style={{
                              marginBottom: '16px',
                              color: '#495057',
                              fontWeight: '600',
                              fontSize: '14px',
                              paddingBottom: '12px',
                              borderBottom: '2px solid #f0f0f0'
                            }}>
                              📊 File Information
                            </h6>
                            <div style={{ display: 'grid', gap: '12px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                                <span style={{ color: '#6c757d', fontWeight: '500' }}>File Type:</span>
                                <span style={{ color: '#212529', fontWeight: '600' }}>{getFileType(previewScaffold)}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
                                <span style={{ color: '#6c757d', fontWeight: '500' }}>Filename:</span>
                                <span style={{ color: '#212529', fontWeight: '600', wordBreak: 'break-word', textAlign: 'right', maxWidth: '60%' }}>
                                  {previewScaffold.Filename || previewScaffold.filename || 'Not specified'}
                                </span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
                                <span style={{ color: '#6c757d', fontWeight: '500' }}>File ID:</span>
                                <span style={{ color: previewScaffold.FileID ? '#28a745' : '#dc3545', fontWeight: '600' }}>
                                  {previewScaffold.FileID ? '✓ Available' : '✗ Not specified'}
                                </span>
                              </div>
                              {previewScaffold.CamtasiaID && previewScaffold.CamtasiaID > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
                                  <span style={{ color: '#6c757d', fontWeight: '500' }}>Camtasia ID:</span>
                                  <span style={{ color: '#212529', fontWeight: '600' }}>{previewScaffold.CamtasiaID}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div style={{
                            border: '2px solid #e9ecef',
                            borderRadius: '8px',
                            padding: '20px',
                            marginBottom: '20px',
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                          }}>
                            <h6 style={{
                              marginBottom: '16px',
                              color: '#495057',
                              fontWeight: '600',
                              fontSize: '14px',
                              paddingBottom: '12px',
                              borderBottom: '2px solid #f0f0f0'
                            }}>
                              📅 Metadata
                            </h6>
                            <div style={{ display: 'grid', gap: '12px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                                <span style={{ color: '#6c757d', fontWeight: '500' }}>Status:</span>
                                <span style={{
                                  color: previewScaffold.Status === 'A' ? '#28a745' : '#dc3545',
                                  fontWeight: '600',
                                  padding: '2px 10px',
                                  backgroundColor: previewScaffold.Status === 'A' ? '#d4edda' : '#f8d7da',
                                  borderRadius: '12px',
                                  fontSize: '12px'
                                }}>
                                  {previewScaffold.Status === 'A' ? '✓ Active' : '✗ Inactive'}
                                </span>
                              </div>
                              {previewScaffold.CreatedDate && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
                                  <span style={{ color: '#6c757d', fontWeight: '500' }}>Created:</span>
                                  <span style={{ color: '#212529', fontWeight: '600' }}>
                                    {new Date(previewScaffold.CreatedDate).toLocaleDateString('en-US', { 
                                      year: 'numeric', 
                                      month: 'short', 
                                      day: 'numeric' 
                                    })}
                                  </span>
                                </div>
                              )}
                              {previewScaffold.ModifiedDate && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
                                  <span style={{ color: '#6c757d', fontWeight: '500' }}>Modified:</span>
                                  <span style={{ color: '#212529', fontWeight: '600' }}>
                                    {new Date(previewScaffold.ModifiedDate).toLocaleDateString('en-US', { 
                                      year: 'numeric', 
                                      month: 'short', 
                                      day: 'numeric' 
                                    })}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer" style={{
                padding: '16px 24px',
                borderTop: '2px solid #e9ecef',
                backgroundColor: '#f8f9fa',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ fontSize: '12px', color: '#6c757d' }}>
                  {previewScaffold.TypeDesc} • {getFileType(previewScaffold).toUpperCase()}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button 
                    type="button" 
                    className="btn btn-success"
                    onClick={() => {
                      handleClosePreview();
                      handleAddScaffold(previewScaffold);
                    }}
                    style={{
                      padding: '8px 20px',
                      fontWeight: '600',
                      borderRadius: '6px',
                      transition: 'all 0.2s'
                    }}
                  >
                    ➕ Add to Topic
                  </button>
                  
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={handleClosePreview}
                    style={{
                      padding: '8px 20px',
                      fontWeight: '600',
                      borderRadius: '6px',
                      transition: 'all 0.2s'
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScaffoldManagementModal;
